import React, { useState, useEffect } from 'react';
import InvoicePreview from './InvoicePreview';
import { FileSearch, ChevronLeft, Search, RefreshCw, Bell, MessageSquare, Trash2 } from 'lucide-react';

const InvoiceHistory = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const userRole = user?.role || user?.Role;
  const clientId = user?.clientId || user?.ClientId;
  const userId = user?.id || user?.Id;

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const fetchInvoices = async () => {
    try {
      const url = userRole === 'SuperAdmin' 
        ? `/api/invoice/list?searchTerm=${encodeURIComponent(searchTerm)}` 
        : `/api/invoice/list?clientId=${clientId}&searchTerm=${encodeURIComponent(searchTerm)}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setInvoices(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const viewInvoice = async (id) => {
    try {
      const res = await fetch(`/api/invoice/${id}`);
      const data = await res.json();
      setSelectedInvoice(data);
    } catch(err) {
      alert("Error fetching invoice details.");
    }
  };

  const toggleStatus = async (inv) => {
    const id = inv.id || inv.Id;
    const currentStatus = inv.paymentStatus || inv.PaymentStatus;
    const newStatus = currentStatus === 'Paid' ? 'Unpaid' : 'Paid';
    
    try {
      const res = await fetch(`/api/invoice/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            status: newStatus,
            userId: userId // For Audit Log
        })
      });
      if (res.ok) {
        fetchInvoices();
      } else {
        alert("Failed to update status.");
      }
    } catch (err) {
      alert("API Error updating status.");
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice? This will remove it from analytics and listings, but it will remain in database audit logs.")) {
      return;
    }
    
    try {
      const res = await fetch(`/api/invoice/${id}?userId=${userId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchInvoices();
      } else {
        alert("Failed to delete invoice.");
      }
    } catch (err) {
      alert("API Error deleting invoice.");
    }
  };

  const sendReminder = async (inv, channel) => {
    try {
        const invNo = inv.invoiceNo || inv.InvoiceNo;
        if (channel === 'WhatsApp') {
            const msg = `Reminder: Payment for Invoice ${invNo} is pending. Please clear it at your earliest convenience. - Intechronix`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
            return;
        }

        const res = await fetch('/api/email/send-reminder', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                invoiceNo: invNo,
                channel: channel,
                to: inv.billToCompany
            })
        });
        if (res.ok) alert(`Reminder request sent via ${channel}`);
    } catch (err) {
        alert("Error sending reminder.");
    }
  };

  if (selectedInvoice) {
    const invoice = selectedInvoice.invoice || selectedInvoice.Invoice;
    const templateComponent = invoice?.templateComponent || invoice?.TemplateComponent || 'TemplateModern';
    return (
      <div className="animate-fade-in">
        <button onClick={() => setSelectedInvoice(null)} className="btn-outline no-print" style={{ display: 'inline-flex', alignItems: 'center', marginBottom: '1.5rem', width: 'auto', padding: '0.5rem 1rem' }}>
          <ChevronLeft size={16} style={{ marginRight: '0.25rem' }} /> Back to History
        </button>
        <InvoicePreview invoiceData={selectedInvoice} templateComponent={templateComponent} />
      </div>
    );
  }

  return (
    <div className="form-section animate-fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h3 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, border: 'none' }}>
          <FileSearch size={22} className="text-primary"/> Recent Invoices
        </h3>
        
        <div style={{ position: 'relative', width: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input 
            type="text" 
            placeholder="Search invoice, client..." 
            className="premium-input" 
            style={{ paddingLeft: '40px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>No invoices found.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="premium-table">
            <thead>
              <tr>
                <th>Invoice No.</th>
                <th>Status</th>
                <th>Date</th>
                <th>Billed To</th>
                {userRole === 'SuperAdmin' && <th>Biller Company</th>}
                <th style={{ textAlign: 'right' }}>Total Amount</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const status = inv.paymentStatus || inv.PaymentStatus || 'Unpaid';
                return (
                  <tr key={inv.id || inv.Id}>
                    <td style={{ fontWeight: '600', color: '#0f172a' }}>{inv.invoiceNo || inv.InvoiceNo}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button 
                            onClick={() => toggleStatus(inv)}
                            style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.7rem', 
                            fontWeight: '700', 
                            background: status === 'Paid' ? '#dcfce7' : '#fee2e2', 
                            color: status === 'Paid' ? '#15803d' : '#b91c1c',
                            border: 'none',
                            cursor: 'pointer'
                            }}
                        >
                            {status.toUpperCase()}
                        </button>
                        {status === 'Unpaid' && (
                            <div style={{ display: 'flex', gap: '2px' }}>
                                <button onClick={() => sendReminder(inv, 'Email')} title="Send Email Reminder" style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer' }}><Bell size={14} /></button>
                                <button onClick={() => sendReminder(inv, 'WhatsApp')} title="Send WhatsApp Reminder" style={{ background: 'none', border: 'none', color: '#25D366', cursor: 'pointer' }}><MessageSquare size={14} /></button>
                            </div>
                        )}
                      </div>
                    </td>
                     <td style={{ fontSize: '0.9rem' }}>
                      {inv.invoiceDate || inv.InvoiceDate 
                        ? new Date(inv.invoiceDate || inv.InvoiceDate).toLocaleDateString() 
                        : 'N/A'}
                    </td>
                    <td>{inv.billToCompany || inv.BillToCompany || 'N/A'}</td>
                    {userRole === 'SuperAdmin' && <td style={{ fontSize: '0.9rem', color: '#64748b' }}>{inv.billerCompany || inv.BillerCompany || 'N/A'}</td>}
                    <td style={{ textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                      ₹{((inv.grandTotal || inv.GrandTotal || 0)).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center' }}>
                        <button onClick={() => viewInvoice(inv.id || inv.Id)} className="btn-outline" style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                          View
                        </button>
                        {userRole === 'SuperAdmin' && (
                          <button 
                            onClick={() => deleteInvoice(inv.id || inv.Id)} 
                            title="Delete Invoice" 
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: 'var(--danger)', 
                              cursor: 'pointer', 
                              padding: '0.4rem', 
                              display: 'inline-flex', 
                              alignItems: 'center',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fee2e2'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceHistory;
